import { fmtFecha } from '../../lib/utils';
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { NumberInput } from '../../components/NumberInput';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Plus, Trash2, Search, Loader2, FileText, Save, Package,
  DollarSign, History, CheckCircle, Building, ExternalLink, ListChecks,
  ClipboardList, Lightbulb, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { CECPStatusChip } from '../../components/ui/CECPStatusChip';
import { SearchableCombobox } from '../../components/ui/searchable-combobox';
import { SolicitanteAutocomplete } from '../../components/compras/SolicitanteAutocomplete';

const fmt = (num) => Math.round(num || 0).toLocaleString('es-CL');

// Autocomplete input for materials
function MaterialAutocomplete({ value, onChange, onSelect, placeholder, expanded }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalMatch, setTotalMatch] = useState(0);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (expanded) return; // Don't close on outside click in expanded mode
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expanded]);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (val.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/compras/materiales?q=${encodeURIComponent(val)}&limit=750`);
        setSuggestions(res.data.materiales || []);
        setTotalMatch(res.data.total_coincidencias || 0);
        setShowSuggestions(true);
      } catch { }
      finally { setLoading(false); }
    }, 300);
  };

  const selectMaterial = (mat) => {
    onSelect(mat);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  return (
    <div className={expanded ? "" : "relative"} ref={wrapperRef}>
      <Input value={value} onChange={handleChange} onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        className={expanded ? "text-sm" : "h-7 text-xs"} placeholder={placeholder || "Escriba para buscar..."} />
      {loading && <Loader2 className={`w-3 h-3 animate-spin absolute ${expanded ? 'right-3 top-3' : 'right-2 top-2'} text-gray-400`} />}
      {(showSuggestions || expanded) && suggestions.length > 0 && (
        <div className={expanded
          ? "mt-2 bg-white border rounded-md max-h-[400px] overflow-y-auto"
          : "absolute z-50 w-full mt-0.5 bg-white border rounded-md shadow-lg max-h-56 overflow-y-auto"}>
          <div className="px-3 py-1 bg-gray-50 border-b text-[10px] text-gray-500 sticky top-0 z-10">
            {totalMatch} material{totalMatch !== 1 ? 'es' : ''} encontrado{totalMatch !== 1 ? 's' : ''}
            {totalMatch > suggestions.length && ` (mostrando ${suggestions.length})`}
          </div>
          {suggestions.map((m, i) => (
            <button key={i} onClick={() => selectMaterial(m)}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 border-b last:border-0 flex justify-between items-center">
              <div className="min-w-0">
                <span className="font-medium">{m.descripcion2 || m.descripcion}</span>
              </div>
              <span className="text-gray-400 text-[10px] shrink-0 ml-2">{m.unidad}</span>
            </button>
          ))}
          {totalMatch > suggestions.length && (
            <div className="px-3 py-1.5 text-[10px] text-blue-600 bg-blue-50 text-center sticky bottom-0">
              Escriba más letras para filtrar los {totalMatch} resultados
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Autocomplete for OT number
//   - `compact`: para usar inline en tablas (sin label, ancho 100% del contenedor).
//   - `onApplyAll`: si está presente y hay valor, muestra botón "↓ a ítems" que copia
//     la OT del header a todas las filas de items (Carlos: evita re-tipear 10 veces).
function OtAutocomplete({ value, onChange, compact = false, onApplyAll = null, onPullFromItems = null, otItems = null }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg] = useState(false);
  const debRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowSugg(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    if (debRef.current) clearTimeout(debRef.current);
    if (val.length < 1) { setSuggestions([]); setShowSugg(false); return; }
    debRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/compras/ots-activas?q=${encodeURIComponent(val)}&solo_abiertas=true&limit=15`);
        setSuggestions(res.data.ots || []);
        setShowSugg(true);
      } catch { setSuggestions([]); }
    }, 300);
  };

  // Iter 290 (Carlos, feb-2026): cuando el parent nos entrega la lista completa
  // (otItems) usamos el SearchableCombobox unificado, igual que en OC Express.
  // Fallback al Input+API cuando no hay lista pre-cargada (compat legacy).
  if (Array.isArray(otItems)) {
    // Iter 292 (Carlos, feb-2026) — En el modo compact (dentro de tabla de ítems)
    // el input es muy angosto, así que sólo mostramos "OT-1234" como label
    // (visible al seleccionar). La descripción y estado van al subLabel del
    // dropdown para no cortar el código de la OT en la celda.
    const itemsMapped = otItems.map((ot) => {
      const desc = ot.descripcion ? ot.descripcion.substring(0, 60) : '';
      const estado = ot.estado || '';
      const sub = compact
        ? [desc, estado].filter(Boolean).join(' · ')
        : estado;
      return {
        value: String(ot.numero_ot),
        label: compact
          ? `OT-${ot.numero_ot}`
          : `OT-${ot.numero_ot}${desc ? ` — ${desc}` : ''}`,
        subLabel: sub,
      };
    });
    return (
      <div ref={wrapRef}>
        {!compact && (
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-medium">OT</label>
            <div className="flex items-center gap-2">
              {onPullFromItems && !value && (
                <button
                  type="button"
                  onClick={onPullFromItems}
                  className="text-[10px] text-blue-600 hover:text-blue-800 underline"
                  title="Tomar la OT del primer ítem que la tenga"
                  data-testid="btn-tomar-ot-de-items"
                >
                  ↑ desde ítems
                </button>
              )}
              {onApplyAll && value && (
                <button
                  type="button"
                  onClick={() => onApplyAll(value)}
                  className="text-[10px] text-blue-600 hover:text-blue-800 underline"
                  title={`Copiar OT ${value} a todos los ítems de la OC`}
                  data-testid="btn-aplicar-ot-a-items"
                >
                  ↓ a ítems
                </button>
              )}
            </div>
          </div>
        )}
        <SearchableCombobox
          items={itemsMapped}
          value={value ? String(value) : ''}
          onChange={(sel) => onChange(sel ? sel.value : '')}
          placeholder={compact ? 'OT' : 'N° OT (escriba para buscar)'}
          emptyMessage="Sin OTs"
          testid={compact ? 'input-ot-item' : 'input-ot'}
          compact={compact}
        />
      </div>
    );
  }

  return (
    <div className="relative" ref={wrapRef}>
      {!compact && (
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-medium">OT</label>
          <div className="flex items-center gap-2">
            {onPullFromItems && !value && (
              <button
                type="button"
                onClick={onPullFromItems}
                className="text-[10px] text-blue-600 hover:text-blue-800 underline"
                title="Tomar la OT del primer ítem que la tenga"
                data-testid="btn-tomar-ot-de-items"
              >
                ↑ desde ítems
              </button>
            )}
            {onApplyAll && value && (
              <button
                type="button"
                onClick={() => onApplyAll(value)}
                className="text-[10px] text-blue-600 hover:text-blue-800 underline"
                title={`Copiar OT ${value} a todos los ítems de la OC`}
                data-testid="btn-aplicar-ot-a-items"
              >
                ↓ a ítems
              </button>
            )}
          </div>
        </div>
      )}
      <Input
        placeholder={compact ? 'OT' : 'N° OT (escriba para buscar)'}
        value={value}
        onChange={handleChange}
        onFocus={() => { if (suggestions.length > 0) setShowSugg(true); }}
        className={compact ? 'h-7 text-xs text-center font-mono' : ''}
        data-testid={compact ? 'input-ot-item' : 'input-ot'}
      />
      {showSugg && suggestions.length > 0 && (
        <div className={`absolute z-50 ${compact ? 'left-0 w-64' : 'w-72'} mt-0.5 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto dark:bg-slate-900`}>
          {suggestions.map((ot) => (
            <button key={ot.numero_ot} type="button"
              onClick={() => { onChange(String(ot.numero_ot)); setShowSugg(false); setSuggestions([]); }}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 dark:hover:bg-blue-950 border-b last:border-0">
              <span className="font-mono font-bold text-blue-700 dark:text-blue-400">OT-{ot.numero_ot}</span>
              <span className={`ml-2 text-[10px] px-1 rounded ${ot.estado === 'EN EJECUCION' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                {ot.estado}
              </span>
              {ot.descripcion && <span className="block text-[10px] text-gray-500 truncate">{ot.descripcion}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Iter 389 — SolicitanteAutocomplete movido a components/compras (compartido con OC Express)

export default function GenerarOC() {
  const navigate = useNavigate();
  const { numeroOc: editNumeroOc } = useParams();
  const isEditMode = !!editNumeroOc;
  // Iter 236.y (Renzo, feb-2026) — Repetir OC: precarga los mismos datos que
  // isEditMode pero SIN bloquear si está aprobada y SIN marcar como edit.
  // Se activa con `?repetir_de=NNN` en la URL. Al guardar → crea una OC NUEVA.
  const repetirDe = new URLSearchParams(window.location.search).get('repetir_de') || '';
  const isRepetirMode = !!repetirDe && !isEditMode;
  const [loadingEdit, setLoadingEdit] = useState(isEditMode || isRepetirMode);
  // Iter 296: OCs históricas (Access/servicios) sin OT pueden editarse sin exigir OT
  const [ocOriginalSinOT, setOcOriginalSinOT] = useState(false);
  // Provider
  const [provSearch, setProvSearch] = useState('');
  const [provResults, setProvResults] = useState([]);
  const [provLoading, setProvLoading] = useState(false);
  const [proveedor, setProveedor] = useState(null);
  const [searched, setSearched] = useState(false); // ya se ejecutó una búsqueda
  const [creandoProv, setCreandoProv] = useState(false);
  const [nuevoProvRut, setNuevoProvRut] = useState('');
  const [nuevoProvNombre, setNuevoProvNombre] = useState('');

  // OC data
  const [otNumero, setOtNumero] = useState('');
  // Iter 290 (feb-2026) — lista completa de OTs abiertas para el SearchableCombobox
  const [otsDropdown, setOtsDropdown] = useState([]);
  // Iter 385 (Renzo) — familias/subfamilias de la malla: obligatoria en ítems sin código de material (de ahí sale la cuenta contable)
  const [subfamilias, setSubfamilias] = useState([]);
  useEffect(() => {
    api.get('/bodega/subfamilias-lista').then((r) => setSubfamilias(r.data.subfamilias || [])).catch(() => {});
  }, []);
  const subfamiliaItems = subfamilias.map((sf) => ({ value: sf.codpf, label: `${sf.familia} › ${sf.subfamilia}`, subLabel: sf.concepto }));
  const [solicitante, setSolicitante] = useState('');
  const [condicionPago, setCondicionPago] = useState('');
  // Iter 292.r (Carlos, feb-2026): rastreamos la condición registrada en el
  // maestro Proveedores para (a) auto-completar al elegir el proveedor y
  // (b) preguntar antes de sobreescribir el maestro cuando el usuario cambia
  // la condición de pago manualmente en la OC.
  const [condicionPagoMaestro, setCondicionPagoMaestro] = useState('');
  const [alegraTermInfo, setAlegraTermInfo] = useState(null);
  const [condicionesVigentes, setCondicionesVigentes] = useState([]); // Iter 286 — 13 condiciones nuevas (CP-*)
  // Iter 286.g (feb-2026): cuando la condición es "Contado", se debe elegir el Medio de Pago.
  const [medioPago, setMedioPago] = useState(''); // codigo del medio de pago seleccionado
  const [mediosPago, setMediosPago] = useState([]); // catálogo activo de medios_pago (habilitado ERP)
  const [observaciones, setObservaciones] = useState('');
  const [numeroDocumento, setNumeroDocumento] = useState('');
  const [tipoDocumento, setTipoDocumento] = useState(''); // factura | guia | boleta | ''
  const [items, setItems] = useState([]);

  // Tipo OC + impuestos (NEW)
  const [tipoOC, setTipoOC] = useState('afecta'); // 'afecta' | 'honorarios' | 'combustible' | 'exenta' | 'mixta'
  // Iter 287.f (Carlos, jul-2026) — Items exentos para tipo "mixta" (facturas
  // como COPEC/CGE con parte afecta y parte exenta). Estructura igual a `items`.
  const [itemsExentos, setItemsExentos] = useState([]);
  const [ivaPorcentaje, setIvaPorcentaje] = useState(19);
  const [retencionPorcentaje, setRetencionPorcentaje] = useState(0);
  const [otrosImpuestos, setOtrosImpuestos] = useState([]); // [{nombre, monto}]

  // Iter 313.f (Renzo, feb-2026) — OC en moneda distinta a CLP.
  const [moneda, setMoneda] = useState('CLP');           // 'CLP' | 'USD' | 'EUR' | 'UF' | 'UTM'
  const [tipoCambio, setTipoCambio] = useState(null);     // valor CLP por unidad de la moneda
  const [tipoCambioFecha, setTipoCambioFecha] = useState(''); // YYYY-MM-DD efectiva del TC
  const [tipoCambioFuente, setTipoCambioFuente] = useState('');
  const [cargandoTC, setCargandoTC] = useState(false);

  // Iter 226: clasificación material vs servicio (decidida desde la OC)
  const [tipoOcClasif, setTipoOcClasif] = useState('material'); // 'material' | 'servicio'
  // Iter 236.m.5 (Carlos): si True, la OC permite asociar MÚLTIPLES facturas
  // del mismo proveedor (típico arriendos, internet, vigilancia, electricidad mensual).
  const [esRecurrente, setEsRecurrente] = useState(false);

  // Material search
  const [matSearch, setMatSearch] = useState('');
  const [_matResults, setMatResults] = useState([]);
  const [_matLoading, setMatLoading] = useState(false);
  const [matModal, setMatModal] = useState(false);

  // Price history
  const [preciosModal, setPreciosModal] = useState({ open: false, loading: false, precios: [], material: '' });

  // Save
  const [saving, setSaving] = useState(false);
  const guardandoRef = useRef(false);
  const [ocCreada, setOcCreada] = useState(null);

  // Requisicion selector
  const [reqModalOpen, setReqModalOpen] = useState(false);
  const [reqList, setReqList] = useState([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [reqSearch, setReqSearch] = useState('');
  const [selectedReqNum, setSelectedReqNum] = useState(null);

  // Sugerencias familia
  const [sugerencias, setSugerencias] = useState([]);
  const [sugFamilias, setSugFamilias] = useState([]);
  const [sugModalOpen, setSugModalOpen] = useState(false);
  const [sugChecked, setSugChecked] = useState({});

  // Load pre-selected items from Pedidos/Requisiciones cart
  useEffect(() => {
    // Iter 286 — cargar las 13 condiciones vigentes (CP-*) del nuevo catálogo
    api.get('/condiciones-pago-crud', { params: { solo_nuevas: true } })
      .then((r) => setCondicionesVigentes(r.data || []))
      .catch(() => setCondicionesVigentes([]));
    // Iter 286.g — catálogo de medios de pago (solo habilitados ERP y activos)
    api.get('/medios-pago')
      .then((r) => {
        const list = (r.data || []).filter((m) => m.habilitado_erp !== false);
        setMediosPago(list);
      })
      .catch(() => setMediosPago([]));
    // Iter 290 — lista completa de OTs abiertas (EN EJECUCION + PENDIENTE)
    // para el SearchableCombobox unificado (mismo que OC Express).
    api.get('/compras/ots-dropdown')
      .then((r) => setOtsDropdown(r.data?.ots || []))
      .catch(() => setOtsDropdown([]));
    // En modo edición o Repetir OC: cargar OC existente y precargar todos los campos
    if (isEditMode || isRepetirMode) {
      (async () => {
        try {
          const sourceOc = isEditMode ? editNumeroOc : repetirDe;
          const res = await api.get(`/compras/ordenes-compra/${sourceOc}`);
          const oc = res.data.orden_compra || {};
          // Iter 236.y: en modo Repetir se PERMITE que la OC origen esté aprobada
          // (justamente lo típico es repetir OCs ya cerradas). No bloqueamos.
          if (isEditMode && (oc.aprobada || oc.estado === 'aprobada')) {
            toast.error('Esta OC ya está aprobada y no puede editarse');
            navigate('/compras/ordenes-compra');
            return;
          }
          // Provider
          setProveedor({
            rut: oc.rut_proveedor || '',
            nombre: oc.nombre_proveedor || oc.proveedor || '',
          });
          setProvSearch(oc.nombre_proveedor || oc.proveedor || '');
          setOtNumero(oc.ot_numero ? String(oc.ot_numero) : '');
          setOcOriginalSinOT(!oc.ot_numero);
          setSolicitante(oc.solicitante || '');
          setCondicionPago(oc.condicion_pago || '');
          setMedioPago(oc.medio_pago_codigo || ''); // Iter 286.g
          setObservaciones(oc.observaciones || '');
          setNumeroDocumento(oc.numero_documento || '');
          setTipoDocumento(oc.tipo_documento || '');
          // Tipo OC + impuestos (NEW, fallback a afecta/19% para OCs viejas)
          setTipoOC(oc.tipo_oc || 'afecta');
          setIvaPorcentaje(oc.iva_porcentaje != null ? oc.iva_porcentaje : 19);
          setRetencionPorcentaje(oc.retencion_porcentaje || 0);
          setOtrosImpuestos(Array.isArray(oc.otros_impuestos) ? oc.otros_impuestos : []);
          // Iter 226: clasificación material/servicio
          setTipoOcClasif(oc.tipo_oc_clasif || 'material');
          setEsRecurrente(!!oc.es_recurrente);
          // Iter 313.f — Cargar moneda + TC persistidos si venían
          setMoneda(oc.moneda || 'CLP');
          if (oc.moneda && oc.moneda !== 'CLP' && oc.tipo_cambio) {
            setTipoCambio(oc.tipo_cambio);
            setTipoCambioFecha(oc.tipo_cambio_fecha || '');
            setTipoCambioFuente(oc.tipo_cambio_fuente || '');
          }
          // Items — Iter 295.a: separar afectos de exentos (Factura Mixta)
          const detalles = res.data.detalles || [];
          const mapDetalle = (d, idx) => ({
            key: Date.now() + idx + Math.random(),
            descripcion: d.descripcion || '',
            codigo_material: d.codigo_material || d.codigo || '',
            unidad: d.unidad || d.unidad_medida || 'UND',
            cantidad: d.cantidad || 0,
            valor_unitario: d.precio_unitario || d.valor_unitario || 0,
            observaciones: d.observaciones || '',
            ot_numero: d.ot_numero || null,
            cuenta_gasto: d.cuenta_gasto || '',
            subfamilia_codpf: d.subfamilia_codpf || '',
            requisicion_numero: d.requisicion_numero || null,
            pedido_origen: d.pedido_origen || '',
            pedido_numero_origen: d.pedido_numero_origen || '',
            pedido_item_origen: d.pedido_item_origen || null,
          });
          setItems(detalles.filter(d => !d.es_exento).map(mapDetalle));
          setItemsExentos(detalles.filter(d => d.es_exento).map(mapDetalle));
          if (isRepetirMode) {
            // Iter 236.y — limpia campos que no deben clonarse a la nueva OC:
            //   * `numero_documento` / `tipo_documento` (la factura llegará después)
            //   * `observaciones` — mantener por defecto pero prefijar con "Repetida de OC #X"
            setNumeroDocumento('');
            setTipoDocumento('');
            setObservaciones(prev => `Repetida de OC #${repetirDe}${prev ? ' · ' + prev : ''}`);
            toast.success(`OC #${repetirDe} clonada. Revisa los datos y guarda como OC nueva.`);
          }
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Error cargando OC');
          navigate('/compras/ordenes-compra');
        } finally {
          setLoadingEdit(false);
        }
      })();
      return; // Skip cart-based preload en modo edición/repetir
    }

    const stored = sessionStorage.getItem('oc_items_from_pedidos');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const preloaded = parsed.map((it, idx) => ({
            key: Date.now() + idx,
            descripcion: it.descripcion || '',
            codigo_material: it.codigo_material || '',
            unidad: it.unidad || 'UND',
            cantidad: Math.max(it.cantidad || 1, 0),
            valor_unitario: 0,
            observaciones: it.observaciones || '',
            ot_numero: it.ot_numero || null,
            cuenta_gasto: it.cuenta_gasto || '',
            subfamilia_codpf: it.subfamilia_codpf || '',
            requisicion_numero: it.requisicion_numero || null,
            pedido_origen: it.pedido_origen || '',
            pedido_numero_origen: it.pedido_numero_origen || '',
            pedido_item_origen: it.pedido_item_origen || null,
          }));
          setItems(preloaded);
          toast.info(`${preloaded.length} items cargados desde Pedidos/Requisiciones`);
        }
      } catch { /* JSON inválido en storage — ignorar y limpiar */ }
      sessionStorage.removeItem('oc_items_from_pedidos');
    }
    // Load solicitante from cart metadata
    const storedMeta = sessionStorage.getItem('oc_meta_from_pedidos');
    if (storedMeta) {
      try {
        const meta = JSON.parse(storedMeta);
        if (meta.solicitante) setSolicitante(meta.solicitante);
      } catch { /* JSON inválido en storage — ignorar y limpiar */ }
      sessionStorage.removeItem('oc_meta_from_pedidos');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buscarProveedor = async () => {
    if (!provSearch.trim()) return;
    setProvLoading(true);
    try {
      const res = await api.get(`/compras/proveedores-lista?q=${encodeURIComponent(provSearch)}`);
      setProvResults(res.data.proveedores || []);
      setSearched(true);
    } catch { toast.error('Error buscando proveedores'); }
    finally { setProvLoading(false); }
  };

  // Valida RUT chileno (formato + dígito verificador, módulo 11).
  const validarRutChileno = (rut) => {
    if (!rut) return false;
    const limpio = rut.replace(/[.\s-]/g, '').toUpperCase();
    if (limpio.length < 2) return false;
    const cuerpo = limpio.slice(0, -1);
    const dv = limpio.slice(-1);
    if (!/^\d+$/.test(cuerpo) || !/^[0-9K]$/.test(dv)) return false;
    let suma = 0, factor = 2;
    for (let i = cuerpo.length - 1; i >= 0; i--) {
      suma += parseInt(cuerpo[i], 10) * factor;
      factor = factor === 7 ? 2 : factor + 1;
    }
    const resto = 11 - (suma % 11);
    const dvCalc = resto === 11 ? '0' : resto === 10 ? 'K' : String(resto);
    return dvCalc === dv;
  };

  // Crea el proveedor directamente en Alegra (fuente de verdad) cuando no existe.
  const crearEnAlegra = async () => {
    const rut = nuevoProvRut.trim();
    const nombre = nuevoProvNombre.trim();
    if (!rut || !nombre) { toast.error('RUT y nombre son obligatorios'); return; }
    if (!validarRutChileno(rut)) { toast.error('Error en RUT. Por favor, revíselo y vuelva a ingresarlo.'); return; }
    setCreandoProv(true);
    try {
      const res = await api.post('/compras/proveedores/crear-en-alegra', { rut, nombre });
      const prov = res.data?.proveedor;
      if (prov) {
        toast.success(res.data?.message || `Proveedor creado en Alegra: ${prov.nombre}`);
        setProveedor(prov);
        setCondicionSiVigente(prov.condicion_pago);
        if (prov.rut) consultarCondicionesPago(prov.rut);
        setProvResults([]); setSearched(false);
        setNuevoProvRut(''); setNuevoProvNombre('');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al crear proveedor en Alegra');
    } finally {
      setCreandoProv(false);
    }
  };

  // RUT del proveedor nuevo inválido (módulo 11) cuando ya se escribió algo.
  const nuevoProvRutInvalido = nuevoProvRut.trim() !== '' && !validarRutChileno(nuevoProvRut.trim());

  // Opción A (Carlos): consultar la situación tributaria del proveedor en el SII.
  // El SII no permite prellenar por URL, así que copiamos el RUT y abrimos el formulario.
  const consultarSII = () => {
    const rut = nuevoProvRut.trim();
    if (!rut) { toast.error('Ingresa el RUT primero para consultarlo en el SII.'); return; }
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(rut);
        toast.success('RUT copiado. Pégalo en la consulta del SII (se abrió en otra pestaña).');
      } else {
        toast.info('Se abrió la consulta del SII. Ingresa el RUT manualmente.');
      }
    } catch {
      toast.info('Se abrió la consulta del SII. Ingresa el RUT manualmente.');
    }
    window.open('https://www2.sii.cl/stc/noauthz', '_blank', 'noopener,noreferrer');
  };

  const consultarCondicionesPago = async (rut) => {
    try {
      const res = await api.get(`/compras/condiciones-pago-proveedor/${encodeURIComponent(rut)}`);
      const { alegra_term, sugerida, sin_equivalente } = res.data;
      if (alegra_term) {
        setAlegraTermInfo({ ...alegra_term, sugerida, sin_equivalente });
        // Iter 365: NUNCA usar el nombre crudo de Alegra ("60 días"); solo su
        // equivalente VIGENTE del catálogo ERP. Iter 293: no pisar el maestro.
        if (sugerida?.nombre) {
          setCondicionPago(prev => prev && prev.trim() ? prev : sugerida.nombre);
        } else if (sin_equivalente) {
          toast.warning(
            `Alegra indica "${alegra_term.nombre}" para este proveedor y el ERP no tiene una condición equivalente. ` +
            'Créala en Configuración → Condiciones de Pago o elige una vigente.',
            { duration: 9000 },
          );
        }
      }
    } catch { }
  };

  // Iter 365 — solo aceptar nombres del catálogo vigente al autocompletar desde proveedor
  const setCondicionSiVigente = (nombre) => {
    if (nombre && condicionesVigentes.some((c) => c.nombre === nombre)) setCondicionPago(nombre);
  };

  const _buscarMaterial = async () => {
    if (!matSearch.trim()) return;
    setMatLoading(true);
    try {
      const res = await api.get(`/compras/materiales?q=${encodeURIComponent(matSearch)}&limit=750`);
      setMatResults(res.data.materiales || []);
    } catch { toast.error('Error buscando materiales'); }
    finally { setMatLoading(false); }
  };

  const verPrecios = async (codigo, descripcion) => {
    setPreciosModal({ open: true, loading: true, precios: [], material: descripcion });
    try {
      const res = await api.get(`/compras/materiales/${encodeURIComponent(codigo)}/precios`);
      setPreciosModal(prev => ({ ...prev, loading: false, precios: res.data.precios || [] }));
    } catch {
      setPreciosModal(prev => ({ ...prev, loading: false }));
    }
  };

  const agregarItem = (mat = null) => {
    setItems(prev => [...prev, {
      key: Date.now(),
      descripcion: mat?.descripcion2 || mat?.descripcion || '',
      codigo_material: mat?.codigo || '',
      unidad: mat?.unidad || 'UND',
      cantidad: 1,
      valor_unitario: 0,
      observaciones: '',
    }]);
    if (mat) {
      setMatModal(false);
      // Trigger family suggestions for the added material
      if (mat.codigo) {
        buscarSugerencias([mat.codigo], null);
      }
    }
  };

  // Load requisiciones for selector
  const loadRequisiciones = async () => {
    setReqLoading(true);
    try {
      const res = await api.get('/compras/requisiciones-para-oc');
      setReqList(res.data.requisiciones || []);
    } catch { toast.error('Error cargando requisiciones'); }
    finally { setReqLoading(false); }
  };

  const cargarRequisicion = (req) => {
    const newItems = req.items.map((it, idx) => ({
      key: Date.now() + idx,
      descripcion: it.descripcion || '',
      codigo_material: it.codigo_material || '',
      unidad: it.unidad || 'UND',
      cantidad: it.cantidad || 1,
      valor_unitario: 0,
      observaciones: it.observaciones ? `Req #${req.numero} - ${it.observaciones}` : `Req #${req.numero}`,
      requisicion_numero: req.numero,
      subfamilia_codpf: it.subfamilia_codpf || '',
      cuenta_gasto: it.cuenta_gasto || '',
    }));
    setItems(prev => [...prev, ...newItems]);
    if (req.solicitante && !solicitante) setSolicitante(req.solicitante);
    if (req.ot_numero && !otNumero) setOtNumero(String(req.ot_numero));
    setSelectedReqNum(req.numero);
    setReqModalOpen(false);
    toast.success(`${newItems.length} items cargados de Req #${req.numero}`);

    // Check for similar family items in other requisiciones
    const codigos = newItems.map(i => i.codigo_material).filter(Boolean);
    if (codigos.length > 0) buscarSugerencias(codigos, req.numero);
  };

  const buscarSugerencias = async (codigos, excluirReq) => {
    try {
      const res = await api.post('/compras/sugerencias-familia', {
        codigos,
        excluir_requisicion: excluirReq || null,
      });
      const sug = res.data.sugerencias || [];
      setSugerencias(sug);
      setSugFamilias(res.data.familias_detectadas || []);
      if (sug.length > 0) {
        const totalItems = sug.reduce((s, g) => s + g.items.length, 0);
        toast.info(`${totalItems} items similares encontrados en otras requisiciones`);
      }
    } catch { }
  };

  const agregarSugeridos = () => {
    const toAdd = [];
    for (const grupo of sugerencias) {
      for (const it of grupo.items) {
        const key = `${it.requisicion}-${it.codigo_material}`;
        if (sugChecked[key]) {
          toAdd.push({
            key: Date.now() + toAdd.length,
            descripcion: it.descripcion,
            codigo_material: it.codigo_material,
            unidad: it.unidad,
            cantidad: it.cantidad,
            valor_unitario: 0,
            observaciones: `Req #${it.requisicion} (sugerido)${it.observaciones ? ' - ' + it.observaciones : ''}`,
            requisicion_numero: it.requisicion,
          });
        }
      }
    }
    if (toAdd.length > 0) {
      setItems(prev => [...prev, ...toAdd]);
      toast.success(`${toAdd.length} items sugeridos agregados`);
    }
    setSugModalOpen(false);
    setSugerencias([]);
  };

  const updateItem = (key, field, value) => {
    setItems(prev => prev.map(i => i.key === key ? { ...i, [field]: value } : i));
  };

  const removeItem = (key) => {
    setItems(prev => prev.filter(i => i.key !== key));
  };

  const neto = items.reduce((s, i) => s + (i.cantidad * i.valor_unitario), 0);
  const iva = Math.round(neto * (Number(ivaPorcentaje) || 0) / 100);
  const retencionMonto = Math.round(neto * (Number(retencionPorcentaje) || 0) / 100);
  const totalOtrosImpuestos = otrosImpuestos.reduce((s, o) => s + (Number(o.monto) || 0), 0);
  // Iter 287.f — Factura Mixta: neto de items exentos (sin IVA) suma al total.
  const netoExento = tipoOC === 'mixta'
    ? itemsExentos.reduce((s, i) => s + (i.cantidad * i.valor_unitario), 0)
    : 0;
  const total = Math.round(neto + iva + netoExento + totalOtrosImpuestos - retencionMonto);

  // Iter 313.f — Trae TC automáticamente cuando cambia la moneda (a algo distinto a CLP).
  useEffect(() => {
    if (moneda === 'CLP') {
      setTipoCambio(null);
      setTipoCambioFecha('');
      setTipoCambioFuente('');
      return;
    }
    let cancelled = false;
    (async () => {
      setCargandoTC(true);
      try {
        const hoy = new Date().toISOString().slice(0, 10);
        const r = await api.get(`/finanzas/tipo-cambio?fecha=${hoy}&moneda=${moneda}`);
        if (!cancelled) {
          setTipoCambio(r.data.valor);
          setTipoCambioFecha(r.data.fecha_efectiva || r.data.fecha);
          setTipoCambioFuente(r.data.fuente || '');
        }
      } catch (err) {
        if (!cancelled) {
          setTipoCambio(null);
          toast.error(`No se pudo obtener TC ${moneda}: ${err.response?.data?.detail || err.message}`);
        }
      } finally {
        if (!cancelled) setCargandoTC(false);
      }
    })();
    return () => { cancelled = true; };
  }, [moneda]);

  const totalCLP = (moneda !== 'CLP' && tipoCambio) ? Math.round(total * tipoCambio) : null;

  // Iter 292.r — sincronizar `condicionPagoMaestro` cuando cambia el proveedor.
  // Este valor se usa como "baseline" para preguntar antes de actualizar el
  // maestro si el usuario cambia la condición en el select.
  useEffect(() => {
    setCondicionPagoMaestro(proveedor?.condicion_pago || '');
  }, [proveedor?.rut, proveedor?.condicion_pago]);

  // Iter 292.r — handler de cambio de condición de pago con confirmación
  // opcional para sincronizar el maestro Proveedores.
  const handleCondicionPagoChange = async (nuevoValor) => {
    setCondicionPago(nuevoValor);
    // Si no hay proveedor cargado, no hay maestro que actualizar.
    if (!proveedor?.rut) return;
    // Si el usuario elige el mismo valor del maestro, no hacer nada.
    if (nuevoValor === condicionPagoMaestro) return;
    // Si el maestro está vacío y el usuario asigna algo nuevo, offer sync.
    // Si el maestro tenía un valor diferente, también preguntar.
    const nombreProv = proveedor.nombre || proveedor.rut;
    const mensaje = condicionPagoMaestro
      ? `¿Actualizar también la Condición de Pago en el Maestro de Proveedores para "${nombreProv}"?\n\nActual en el Maestro: ${condicionPagoMaestro}\nNueva: ${nuevoValor || '(vacía)'}`
      : `¿Registrar "${nuevoValor}" como Condición de Pago por defecto para "${nombreProv}" en el Maestro de Proveedores?`;
    if (!nuevoValor) return; // no persistir vacío
    if (!window.confirm(mensaje)) return;
    try {
      // Enviar código si existe (mejor: resuelve el catálogo con dias_plazo).
      const cpItem = condicionesVigentes.find((c) => c.nombre === nuevoValor);
      const payload = cpItem?.codigo
        ? { condicion_pago_codigo: cpItem.codigo }
        : { condicion_pago: nuevoValor };
      await api.patch(`/proveedores/by-rut/${encodeURIComponent(proveedor.rut)}/condicion-pago`, payload);
      setCondicionPagoMaestro(nuevoValor);
      toast.success('Condición de Pago actualizada en el Maestro de Proveedores');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo actualizar el Maestro');
    }
  };

  // Iter 286.g — detectar si la condición seleccionada requiere Medio de Pago.
  // Iter 286.x (Carlos, feb-2026): también "Tarjeta de Crédito" abre el selector
  // pero filtrado a MP de categoría `tarjeta_credito`.
  // Nota: el value del select es c.nombre, así que resolvemos vs el catálogo cargado.
  const condicionSeleccionada = condicionesVigentes.find((c) => c.nombre === condicionPago);
  const isCondicionContado = condicionSeleccionada?.tipo === 'contado';
  const isCondicionTarjeta = condicionSeleccionada?.tipo === 'tarjeta';
  const requiereMedioPago = isCondicionContado || isCondicionTarjeta;
  // Iter 374 (Renzo): al GRABAR, la condición debe ser vigente SIEMPRE (sin tolerancia para OCs antiguas).
  const condicionEsVigente = !!condicionSeleccionada;

  // Si el usuario cambia la condición y ya no requiere MP → limpiamos el medio de pago
  // para no dejar datos inconsistentes en el submit.
  useEffect(() => {
    if (!requiereMedioPago && medioPago) setMedioPago('');
  }, [requiereMedioPago, medioPago]);

  // Cambio de tipo OC: ajustar valores por defecto
  const handleChangeTipoOC = (nuevo) => {
    setTipoOC(nuevo);
    if (nuevo === 'afecta') {
      setIvaPorcentaje(19); setRetencionPorcentaje(0); setOtrosImpuestos([]);
      setItemsExentos([]);
    } else if (nuevo === 'honorarios') {
      setIvaPorcentaje(0); setRetencionPorcentaje(15.25); setOtrosImpuestos([]);
      setItemsExentos([]);
    } else if (nuevo === 'combustible') {
      setIvaPorcentaje(19); setRetencionPorcentaje(0);
      setItemsExentos([]);
      // Mantener otrosImpuestos si existen, sino arrancar lista vacía
    } else if (nuevo === 'exenta') {
      setIvaPorcentaje(0); setRetencionPorcentaje(0); setOtrosImpuestos([]);
      setItemsExentos([]);
    } else if (nuevo === 'mixta') {
      // Iter 287.f — Factura Mixta (afecto + exento en una sola OC).
      // Los items principales SIEMPRE llevan IVA 19% (bloque afecto). El
      // bloque exento se muestra debajo y NO se calcula IVA sobre él.
      setIvaPorcentaje(19); setRetencionPorcentaje(0); setOtrosImpuestos([]);
    }
  };

  // Iter 287.f — Handlers items exentos (misma API que `items` regulares)
  const agregarItemExento = () => {
    setItemsExentos(prev => [...prev, {
      key: Date.now() + Math.random(),
      descripcion: '', codigo_material: '', unidad: 'UND',
      cantidad: 1, valor_unitario: 0, observaciones: '',
      ot_numero: otNumero || null,
    }]);
  };
  const updateItemExento = (key, field, value) => {
    setItemsExentos(prev => prev.map(i => i.key === key ? { ...i, [field]: value } : i));
  };
  const removeItemExento = (key) => {
    setItemsExentos(prev => prev.filter(i => i.key !== key));
  };

  const addOtroImpuesto = () => setOtrosImpuestos(prev => [...prev, { nombre: '', monto: 0 }]);
  const updateOtroImpuesto = (idx, field, value) => {
    setOtrosImpuestos(prev => prev.map((o, i) => i === idx ? { ...o, [field]: field === 'monto' ? (parseFloat(value) || 0) : value } : o));
  };
  const removeOtroImpuesto = (idx) => setOtrosImpuestos(prev => prev.filter((_, i) => i !== idx));

  const handleGuardar = async () => {
    const errores = [];
    if (!proveedor) errores.push('Seleccione un proveedor');
    // Iter 283 (Carlos): datos críticos obligatorios — jamás grabar OC sin identificación del proveedor
    else {
      if (!(proveedor.rut || proveedor.identificacion || '').trim()) {
        errores.push('⛔ El proveedor seleccionado no tiene RUT — complételo en el maestro de Proveedores antes de grabar la OC');
      }
      if (!(proveedor.nombre || '').trim()) {
        errores.push('⛔ El proveedor seleccionado no tiene nombre');
      }
    }
    // Iter 287.f (Carlos, jul-2026) — OT y Condición de Pago obligatorias.
    // La OT debe existir en el ERP y estar abierta (validación final del backend).
    // Iter 296: excepción en modo edición cuando la OC original NO tenía OT
    // (históricas de Access / servicios) — no bloquear su corrección.
    // Iter 389 (Renzo): la OT general se exige, salvo que CADA ítem traiga su propia OT (OC multi-OT).
    const itemsSinOT = [...items, ...(tipoOC === 'mixta' ? itemsExentos : [])].filter(i => !String(i.ot_numero || '').trim());
    const todosItemsConOT = items.length > 0 && itemsSinOT.length === 0;
    if (!otNumero || !String(otNumero).trim()) {
      if (!(isEditMode && ocOriginalSinOT) && !todosItemsConOT) {
        errores.push(`⛔ La OT es obligatoria — indica la OT general o una OT en cada ítem${items.length ? ` (${itemsSinOT.length} ítem(s) sin OT)` : ''}`);
      }
    } else if (!/^\d+$/.test(String(otNumero).trim())) {
      errores.push('⛔ La OT debe ser numérica (número de OT del ERP)');
    }
    // Iter 389 (Renzo) — Solicitante obligatorio (también en OC Express y demás vías; el backend lo exige igual).
    if (!solicitante || !solicitante.trim()) {
      errores.push('⛔ El Solicitante es obligatorio');
    }
    if (!condicionPago || !condicionPago.trim()) {
      errores.push('⛔ La Condición de Pago es obligatoria');
    } else if (!condicionEsVigente) {
      errores.push(`⛔ La condición "${condicionPago}" está inactiva. Seleccione una condición vigente del catálogo`);
    }
    if (items.length === 0) errores.push('Agregue al menos un item');
    const sinDesc = items.filter(i => !i.descripcion?.trim());
    if (sinDesc.length > 0) errores.push(`${sinDesc.length} item(s) sin descripcion`);
    const sinPrecio = items.filter(i => !i.valor_unitario || i.valor_unitario <= 0);
    if (sinPrecio.length > 0) errores.push(`${sinPrecio.length} item(s) sin precio unitario`);
    const sinCantidad = items.filter(i => !i.cantidad || i.cantidad <= 0);
    if (sinCantidad.length > 0) errores.push(`${sinCantidad.length} item(s) sin cantidad`);
    // Iter 385 (Renzo) — ítems sin código de material deben tener Familia (de ahí sale la cuenta contable)
    const sinFamilia = items.filter(i => !i.codigo_material && !i.subfamilia_codpf && !i.cuenta_gasto);
    if (sinFamilia.length > 0) errores.push(`⛔ ${sinFamilia.length} ítem(s) sin Familia — selecciónala en cada ítem sin código de material`);
    // Iter 287.f — Validaciones específicas de Factura Mixta
    if (tipoOC === 'mixta') {
      if (itemsExentos.length === 0) {
        errores.push('⛔ Factura Mixta: agregue al menos un ítem al bloque EXENTO (o cambie el tipo de OC)');
      }
      const exSinDesc = itemsExentos.filter(i => !i.descripcion?.trim());
      if (exSinDesc.length > 0) errores.push(`${exSinDesc.length} ítem(s) exento(s) sin descripción`);
      const exSinPrecio = itemsExentos.filter(i => !i.valor_unitario || i.valor_unitario <= 0);
      if (exSinPrecio.length > 0) errores.push(`${exSinPrecio.length} ítem(s) exento(s) sin precio`);
      const exSinCantidad = itemsExentos.filter(i => !i.cantidad || i.cantidad <= 0);
      if (exSinCantidad.length > 0) errores.push(`${exSinCantidad.length} ítem(s) exento(s) sin cantidad`);
    }
    // Iter 286.g / 286.x — si la condición requiere Medio de Pago (Contado o Tarjeta), es obligatorio
    if (requiereMedioPago && !medioPago) {
      errores.push(`Si la condición es "${isCondicionContado ? 'Contado' : 'Tarjeta de Crédito'}" debes seleccionar un Medio de Pago`);
    }
    if (errores.length > 0) {
      toast.error(errores.join(' · '));
      return;
    }

    // Guard síncrono anti doble-submit: el botón se deshabilita con `saving`, pero
    // recién en setSaving(true) y hay awaits antes (pre-chequeo CECP). Un doble-click
    // rápido dispararía dos envíos concurrentes → el backend creaba folios/OC duplicados.
    // Este ref bloquea de inmediato el segundo click (y se resetea en todos los paths).
    if (guardandoRef.current) return;
    guardandoRef.current = true;

    // Pre-chequeo CECP: evitar submit si proveedor bloqueado para OC
    if (!isEditMode) {
      try {
        const rut = proveedor.rut || proveedor.identificacion;
        if (rut) {
          const st = await api.get('/conciliacion/estado-proveedor', {
            params: { rut, proveedor_id: proveedor.id },
          });
          const estado = st.data?.estado;
          if (estado === 'bloqueado_oc' || estado === 'bloqueado_ambos') {
            toast.error(
              `⛔ No se puede emitir OC — ${st.data?.mensaje || 'Proveedor bloqueado por CECP'}`,
              { duration: 8000 }
            );
            guardandoRef.current = false;
            return;
          }
          if (estado === 'advertencia') {
            toast.warning(
              `⚠ ${st.data?.mensaje} — la OC se emitirá igual por ahora.`,
              { duration: 6000 }
            );
          }
        }
      } catch {
        /* fallback: dejamos que el backend valide con 409 */
      }
    }

    setSaving(true);
    try {
      const payload = {
        rut_proveedor: proveedor.rut || proveedor.identificacion || '',
        nombre_proveedor: proveedor.nombre,
        ot_numero: otNumero ? parseInt(otNumero) : null,
        condicion_pago: condicionPago,
        // Iter 286.g / 286.x — medio de pago obligatorio si Contado o Tarjeta de Crédito
        medio_pago_codigo: requiereMedioPago ? medioPago : '',
        // Iter 226: clasificación material/servicio
        tipo_oc_clasif: tipoOcClasif,
        es_recurrente: esRecurrente,
        solicitante,
        observaciones,
        numero_documento: (numeroDocumento || '').trim(),
        tipo_documento: tipoDocumento || '',
        tipo_oc: tipoOC,
        iva_porcentaje: Number(ivaPorcentaje) || 0,
        retencion_porcentaje: Number(retencionPorcentaje) || 0,
        otros_impuestos: otrosImpuestos
          .filter(o => (o.nombre || '').trim() && Number(o.monto || 0) !== 0)
          .map(o => ({ nombre: (o.nombre || '').trim(), monto: Number(o.monto) || 0 })),
        items: items.map(i => ({
          descripcion: i.descripcion,
          cantidad: parseFloat(i.cantidad) || 1,
          unidad: i.unidad,
          valor_unitario: parseFloat(i.valor_unitario) || 0,
          observaciones: i.observaciones,
          codigo_material: i.codigo_material,
          ot_numero: i.ot_numero ? parseInt(i.ot_numero) : (otNumero ? parseInt(otNumero) : null),
          cuenta_gasto: i.cuenta_gasto || '',
          subfamilia_codpf: i.subfamilia_codpf || '',
          requisicion_numero: i.requisicion_numero || null,
          pedido_origen: i.pedido_origen || '',
          pedido_numero_origen: i.pedido_numero_origen || '',
          pedido_item_origen: i.pedido_item_origen || null,
        })),
        // Iter 287.f — Factura Mixta: enviar items exentos si aplica
        items_exentos: tipoOC === 'mixta'
          ? itemsExentos.map(i => ({
              descripcion: i.descripcion,
              cantidad: parseFloat(i.cantidad) || 1,
              unidad: i.unidad || 'UND',
              valor_unitario: parseFloat(i.valor_unitario) || 0,
              observaciones: i.observaciones || '',
              codigo_material: i.codigo_material || '',
              ot_numero: i.ot_numero ? parseInt(i.ot_numero) : (otNumero ? parseInt(otNumero) : null),
              cuenta_gasto: '',
              requisicion_numero: null,
              pedido_origen: '',
              pedido_numero_origen: '',
              pedido_item_origen: null,
            }))
          : [],
        // Iter 313.f — moneda + TC (si != CLP)
        moneda,
        tipo_cambio: moneda !== 'CLP' ? tipoCambio : null,
        tipo_cambio_fecha: moneda !== 'CLP' ? tipoCambioFecha : null,
      };
      const res = isEditMode
        ? await api.put(`/compras/ordenes-compra/${editNumeroOc}/editar`, payload)
        : await api.post('/compras/generar-oc', payload);
      setOcCreada(res.data);
      toast.success(res.data.message);
      if (isEditMode) {
        // Volver al listado después de editar
        setTimeout(() => navigate('/compras/ordenes-compra'), 1200);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error generando OC');
    } finally { setSaving(false); guardandoRef.current = false; }
  };

  const resetForm = () => {
    setProveedor(null); setProvSearch(''); setProvResults([]); setSearched(false);
    setItems([]); setOtNumero(''); setSolicitante('');
    setCondicionPago(''); setObservaciones('');
    setMedioPago(''); // Iter 286.g
    setNumeroDocumento(''); setTipoDocumento('');
    setTipoOcClasif('material');
    setEsRecurrente(false);
    setOcCreada(null);
    // Iter 287.f — Reset del bloque Factura Mixta
    setItemsExentos([]);
    setTipoOC('afecta');
  };

  return (
    <div className="space-y-5" data-testid={isEditMode ? "editar-oc-page" : "generar-oc-page"}>
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText className="w-6 h-6 text-blue-600" />
          {isEditMode ? `Editar OC #${editNumeroOc}` : 'Generar Orden de Compra'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isEditMode
            ? 'Modifica los campos y guarda. Solo funciona para OCs pendientes de VB.'
            : 'Cree una nueva OC seleccionando proveedor, materiales y precios.'}
        </p>
      </div>

      {loadingEdit && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
          <span className="text-sm text-muted-foreground">Cargando OC {editNumeroOc}…</span>
        </div>
      )}

      {ocCreada && (
        <div className="bg-green-50 border-2 border-green-300 rounded-lg p-5 text-center" data-testid="oc-creada-panel">
          <CheckCircle className="w-10 h-10 mx-auto text-green-600 mb-2" />
          <p className="font-bold text-green-800 text-lg">OC #{ocCreada.numero_oc} Creada</p>
          <p className="text-sm text-green-700 mb-4">Neto: ${fmt(ocCreada.neto)} | IVA: ${fmt(ocCreada.iva)} | Total: ${fmt(ocCreada.total)}</p>
          <div className="flex flex-wrap gap-2 justify-center">
            <Button onClick={() => navigate('/compras/ordenes-compra')} variant="outline" data-testid="btn-ver-listado-oc">
              <FileText className="w-4 h-4 mr-1" /> Ver en Listado OC
            </Button>
            <Button onClick={() => navigate('/compras/seguimiento')} variant="outline" data-testid="btn-ver-seguimiento">
              <ListChecks className="w-4 h-4 mr-1" /> Seguimiento OC
            </Button>
            <Button onClick={resetForm} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-crear-otra">
              <Plus className="w-4 h-4 mr-1" /> Crear otra OC
            </Button>
          </div>
        </div>
      )}

      {!ocCreada && (
        <>
          {/* Provider selection */}
          <div className="bg-white border rounded-lg p-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <Building className="w-4 h-4" /> 1. Seleccionar Proveedor
            </h3>
            {proveedor ? (
              <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded p-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium">{proveedor.nombre}</p>
                    <CECPStatusChip rut={proveedor.rut || proveedor.identificacion} proveedorId={proveedor.id} />
                  </div>
                  <p className="text-xs text-gray-500">{proveedor.rut || proveedor.identificacion}
                    {proveedor.condicion_pago && <span className="ml-2 text-blue-600">({proveedor.condicion_pago})</span>}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => { setProveedor(null); setCondicionPago(''); setSearched(false); setProvResults([]); }}>Cambiar</Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input placeholder="Buscar por nombre o RUT..." value={provSearch}
                  onChange={(e) => setProvSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && buscarProveedor()}
                  className="max-w-md" data-testid="prov-search" />
                <Button onClick={buscarProveedor} disabled={provLoading} variant="outline">
                  {provLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
            )}
            {provResults.length > 0 && !proveedor && (
              <div className="mt-2 max-h-64 overflow-y-auto border rounded">
                {provResults.map((p, i) => {
                  const rutKey = p.rut || p.identificacion;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => {
                        setProveedor(p);
                        setProvResults([]);
                        setSearched(false);
                        setCondicionSiVigente(p.condicion_pago);
                        const rut = p.rut || p.identificacion;
                        if (rut) consultarCondicionesPago(rut);
                      }}
                      className="w-full border-b last:border-b-0 text-left px-3 py-2 text-sm hover:bg-blue-50 flex flex-col gap-0.5"
                      data-testid={`prov-result-${rutKey}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{p.nombre}</span>
                        {p.condicion_pago && (
                          <span className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded">
                            {p.condicion_pago}
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] text-gray-500">{rutKey}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {/* Sin resultados en Alegra -> crear el proveedor en Alegra (fuente de verdad) */}
            {searched && provResults.length === 0 && !proveedor && (
              <div className="mt-2 border rounded p-3 bg-amber-50/50 border-amber-200" data-testid="crear-prov-alegra-box">
                <p className="text-sm text-amber-900 font-medium flex items-center gap-1">
                  <Lightbulb className="w-4 h-4" /> No se encontró el proveedor en Alegra
                </p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Créalo en Alegra para usarlo en la OC (Alegra es la fuente de verdad de proveedores).
                </p>
                <div className="flex flex-col sm:flex-row gap-2 mt-2 sm:items-start">
                  <div className="sm:max-w-[180px] w-full">
                    <Input
                      placeholder="RUT (ej. 76123456-7)"
                      value={nuevoProvRut}
                      onChange={(e) => setNuevoProvRut(e.target.value)}
                      className={nuevoProvRutInvalido ? 'border-red-500 focus-visible:ring-red-500' : ''}
                      aria-invalid={nuevoProvRutInvalido}
                      data-testid="nuevo-prov-rut"
                    />
                    {nuevoProvRutInvalido && (
                      <p className="text-xs text-red-600 mt-1" data-testid="nuevo-prov-rut-error">
                        Error en RUT. Por favor, revíselo y vuelva a ingresarlo.
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={consultarSII}
                      className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-1"
                      data-testid="btn-consultar-sii"
                    >
                      <ExternalLink className="w-3 h-3" /> Consultar situación tributaria en SII
                    </button>
                  </div>
                  <Input
                    placeholder="Razón social"
                    value={nuevoProvNombre}
                    onChange={(e) => setNuevoProvNombre(e.target.value)}
                    className="flex-1"
                    data-testid="nuevo-prov-nombre"
                  />
                  <Button
                    onClick={crearEnAlegra}
                    disabled={creandoProv || nuevoProvRutInvalido}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="btn-crear-prov-alegra"
                  >
                    {creandoProv ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear en Alegra'}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* OC header data */}
          <div className="bg-white border rounded-lg p-4">
            <h3 className="font-semibold text-sm mb-3">2. Datos de la OC</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <OtAutocomplete
                value={otNumero}
                onChange={setOtNumero}
                otItems={otsDropdown}
                onApplyAll={(ot) => {
                  if (!ot) return;
                  setItems(prev => prev.map(i => ({ ...i, ot_numero: ot })));
                  toast.success(`OT ${ot} aplicada a ${items.length} ítem(s)`);
                }}
                onPullFromItems={() => {
                  const otFromItem = items.find(i => i.ot_numero)?.ot_numero;
                  if (otFromItem) {
                    setOtNumero(String(otFromItem));
                    toast.success(`OT ${otFromItem} tomada de los ítems`);
                  }
                }}
              />
              <SolicitanteAutocomplete value={solicitante} onChange={setSolicitante} />
              <div>
                <label className="block text-xs font-medium mb-1">
                  Condicion de Pago
                  {alegraTermInfo && (
                    <span
                      className={`ml-1 text-[10px] font-normal px-1 rounded ${alegraTermInfo.sin_equivalente ? 'text-red-700 bg-red-50 border border-red-200' : 'text-blue-600 bg-blue-50'}`}
                      data-testid="alegra-term-badge"
                    >
                      Alegra: {alegraTermInfo.nombre}
                      {alegraTermInfo.sugerida ? ` → ${alegraTermInfo.sugerida.nombre}` : alegraTermInfo.sin_equivalente ? ' (sin equivalente en ERP)' : ''}
                    </span>
                  )}
                </label>
                {/* Iter 286 — SOLO las 13 condiciones vigentes (CP-*). Las históricas quedan ocultas. */}
                <select value={condicionPago} onChange={(e) => handleCondicionPagoChange(e.target.value)}
                  className={`w-full h-9 px-3 rounded-md border text-sm ${condicionPago && !condicionEsVigente ? 'border-red-500 bg-red-50/40' : 'border-input'}`}
                  data-testid="select-condicion-pago">
                  <option value="">Seleccione...</option>
                  {/* 13 condiciones vigentes del nuevo catálogo */}
                  {condicionesVigentes.map((c) => (
                    <option key={c.codigo} value={c.nombre}>
                      {c.nombre}{c.dias_plazo ? ` (${c.dias_plazo} días)` : ''}
                    </option>
                  ))}
                  {/* Iter 365/374 — la condición legada se muestra deshabilitada: hay que elegir una vigente para grabar. */}
                  {condicionPago && !condicionEsVigente && (
                    <option value={condicionPago} disabled>
                      {condicionPago} (INACTIVA — elija una vigente)
                    </option>
                  )}
                </select>
                {condicionPago && !condicionEsVigente && (
                  <p className="mt-1 text-[11px] text-red-700 font-medium" data-testid="condicion-inactiva-warning">
                    ⛔ "{condicionPago}" es una condición inactiva. Debes seleccionar una condición vigente antes de guardar.
                  </p>
                )}
                {/* Iter 286.g / 286.x — al elegir "Contado" o "Tarjeta de Crédito" se despliega el selector de Medio de Pago */}
                {requiereMedioPago && (
                  <div className="mt-2" data-testid="medio-pago-container">
                    <label className="block text-xs font-medium mb-1">
                      Medio de Pago <span className="text-red-600">*</span>
                      <span className="ml-1 text-[10px] font-normal text-slate-500">
                        {isCondicionTarjeta ? '(sólo tarjetas)' : '(según medio real de pago)'}
                      </span>
                    </label>
                    <select
                      value={medioPago}
                      onChange={(e) => setMedioPago(e.target.value)}
                      className={`w-full h-9 px-3 rounded-md border text-sm ${!medioPago ? 'border-red-400 bg-red-50/40' : 'border-input'}`}
                      data-testid="select-medio-pago"
                    >
                      <option value="">Seleccione medio de pago...</option>
                      {(() => {
                        // Iter 286.x — cuando la condición es "Tarjeta de Crédito" mostrar SOLO
                        // los MP de categoría `tarjeta_credito`. Para "Contado" mostrar todas
                        // las categorías (excluyendo tarjetas — se usan solo con condición tarjeta).
                        const catsContado = [
                          { key: 'transferencia_anticipo', label: 'Transferencia / Anticipo' },
                          { key: 'rendicion', label: 'Rendición (Caja Chica)' },
                          { key: 'otro', label: 'Otro' },
                        ];
                        const catsTarjeta = [
                          { key: 'tarjeta_credito', label: 'Tarjeta de Crédito' },
                        ];
                        const cats = isCondicionTarjeta ? catsTarjeta : catsContado;
                        return cats.map((cat) => {
                          const items = mediosPago.filter((m) => (m.categoria || 'otro') === cat.key);
                          if (items.length === 0) return null;
                          return (
                            <optgroup key={cat.key} label={cat.label}>
                              {items.map((m) => (
                                <option key={m.codigo} value={m.codigo}>{m.nombre}</option>
                              ))}
                            </optgroup>
                          );
                        });
                      })()}
                    </select>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Obligatorio cuando la condición es {isCondicionContado ? 'Contado' : 'Tarjeta de Crédito'}. El pago se contabilizará contra este medio.
                    </p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">N° Documento (recibido)</label>
                <div className="flex gap-1">
                  <select
                    value={tipoDocumento}
                    onChange={(e) => setTipoDocumento(e.target.value)}
                    className="h-9 px-2 rounded-md border border-input text-xs"
                    data-testid="select-tipo-documento"
                  >
                    <option value="">—</option>
                    <option value="factura">Factura</option>
                    <option value="guia">Guía</option>
                    <option value="boleta">Boleta</option>
                  </select>
                  <Input
                    placeholder={tipoDocumento ? `N° ${tipoDocumento}` : 'N°'}
                    value={numeroDocumento}
                    onChange={(e) => setNumeroDocumento(e.target.value)}
                    className="text-xs"
                    data-testid="input-numero-documento"
                  />
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5">Si ya tienes la factura/guía/boleta del proveedor a la vista.</p>
              </div>
            </div>

            <div className="mt-3">
              <label className="block text-xs font-medium mb-1">Observaciones</label>
              <Textarea
                placeholder="Comentario adicional para el proveedor o referencia interna…"
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                rows={2}
                className="text-sm"
                data-testid="input-observaciones-oc"
              />
            </div>

            {/* Iter 226: Clasificación Material vs Servicio (decide si requiere recepción) */}
            <div className="mt-3 pt-3 border-t" data-testid="tipo-oc-clasif-section">
              <label className="text-xs font-medium text-gray-600 block mb-2">
                ¿Requiere recepción de bodega?
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setTipoOcClasif('material')}
                  data-testid="clasif-material"
                  className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 text-left ${
                    tipoOcClasif === 'material'
                      ? 'border-blue-500 bg-blue-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <Package className={`w-5 h-5 flex-shrink-0 ${tipoOcClasif === 'material' ? 'text-blue-600' : 'text-gray-400'}`} />
                  <div>
                    <p className={`text-sm font-semibold ${tipoOcClasif === 'material' ? 'text-blue-700' : 'text-gray-700'}`}>
                      Con Recepción de Bodega
                    </p>
                    <p className="text-[10px] text-gray-500">Materiales que llegan físicamente.</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setTipoOcClasif('servicio')}
                  data-testid="clasif-servicio"
                  className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 text-left ${
                    tipoOcClasif === 'servicio'
                      ? 'border-purple-500 bg-purple-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <Building className={`w-5 h-5 flex-shrink-0 ${tipoOcClasif === 'servicio' ? 'text-purple-600' : 'text-gray-400'}`} />
                  <div>
                    <p className={`text-sm font-semibold ${tipoOcClasif === 'servicio' ? 'text-purple-700' : 'text-gray-700'}`}>
                      Servicio (sin recepción)
                    </p>
                    <p className="text-[10px] text-gray-500">Arriendo, consultoría, electricidad, etc.</p>
                  </div>
                </button>
              </div>
              {/* Iter 236.m.5 (Carlos): Toggle "OC Recurrente" — permite asociar múltiples
                  facturas (servicios mensuales). Solo aplica cuando tipo=servicio. */}
              {tipoOcClasif === 'servicio' && (
                <label
                  className={`mt-3 flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    esRecurrente ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  data-testid="oc-recurrente-toggle"
                >
                  <input
                    type="checkbox"
                    checked={esRecurrente}
                    onChange={(e) => setEsRecurrente(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-amber-500"
                    data-testid="oc-recurrente-checkbox"
                  />
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${esRecurrente ? 'text-amber-800' : 'text-gray-700'}`}>
                      OC recurrente (admite varias facturas)
                    </p>
                    <p className="text-[10px] text-gray-600 mt-0.5">
                      Marca esta opción si vas a recibir facturas mensuales del mismo proveedor
                      contra esta misma OC (arriendo, internet, vigilancia, electricidad…).
                      Si NO la marcas, el sistema bloqueará la asociación de una segunda factura.
                    </p>
                  </div>
                </label>
              )}
            </div>

            {/* Iter 313.f — Selector de Moneda (OC no-CLP) */}
            <div className="mt-3 pt-3 border-t" data-testid="moneda-section">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <label className="text-xs font-medium text-gray-600">Moneda:</label>
                {['CLP','USD','EUR','UF','UTM'].map(m => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMoneda(m)}
                    data-testid={`btn-moneda-${m}`}
                    className={`text-xs px-3 h-8 rounded-md border transition-colors ${
                      moneda === m
                        ? 'bg-emerald-600 text-white border-emerald-700'
                        : 'bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50'
                    }`}
                  >{m}</button>
                ))}
                {moneda !== 'CLP' && (
                  <span className="text-[11px] text-gray-600 ml-2" data-testid="tc-info">
                    {cargandoTC
                      ? 'Cargando TC…'
                      : tipoCambio
                        ? <>TC {moneda}→CLP: <strong className="text-emerald-700 font-mono">${fmt(tipoCambio)}</strong> <span className="text-gray-400">({tipoCambioFecha} · {tipoCambioFuente})</span></>
                        : <span className="text-red-600">TC no disponible</span>}
                  </span>
                )}
              </div>
              {moneda !== 'CLP' && tipoCambio && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block" data-testid="moneda-referencial-note">
                  ⓘ El valor CLP es referencial de acuerdo al TC vigente a la fecha de la OC.
                  Equivalente aprox: <strong className="font-mono">${fmt(totalCLP)} CLP</strong>
                </div>
              )}
            </div>

            {/* Selector Tipo OC + Impuestos */}
            <div className="mt-3 pt-3 border-t" data-testid="tipo-oc-section">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <label className="text-xs font-medium text-gray-600">Tipo de OC:</label>
                {[
                  { key: 'afecta', label: 'Factura Afecta', color: 'indigo' },
                  { key: 'honorarios', label: 'Boleta Honorarios', color: 'purple' },
                  { key: 'combustible', label: 'Factura Combustible', color: 'orange' },
                  { key: 'exenta', label: 'Factura Exenta', color: 'gray' },
                  { key: 'mixta', label: 'Factura Mixta', color: 'teal' },
                ].map(t => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => handleChangeTipoOC(t.key)}
                    data-testid={`tipo-oc-${t.key}`}
                    className={`text-xs px-3 h-8 rounded-md border transition-colors ${
                      tipoOC === t.key
                        ? `bg-${t.color}-600 text-white border-${t.color}-700`
                        : `bg-white text-${t.color}-700 border-${t.color}-200 hover:bg-${t.color}-50`
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <label className="block font-medium mb-1">% IVA</label>
                  <Input type="number" min="0" max="100" step="0.01" value={ivaPorcentaje}
                    onChange={(e) => setIvaPorcentaje(e.target.value)}
                    className="h-8" data-testid="input-iva-porcentaje" />
                </div>
                {tipoOC === 'honorarios' && (
                  <div>
                    <label className="block font-medium mb-1 text-purple-700">% Retención</label>
                    <Input type="number" min="0" max="100" step="0.01" value={retencionPorcentaje}
                      onChange={(e) => setRetencionPorcentaje(e.target.value)}
                      className="h-8 border-purple-300" data-testid="input-retencion-porcentaje"
                      placeholder="15.25" />
                    <p className="text-[10px] text-gray-500 mt-0.5">Se descuenta del subtotal.</p>
                  </div>
                )}
              </div>
              {/* Otros impuestos (Combustible) */}
              {tipoOC === 'combustible' && (
                <div className="mt-3" data-testid="otros-impuestos-section">
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-orange-700">Otros impuestos (IEPD, MEPCO, etc.)</label>
                    <Button type="button" size="sm" variant="outline" onClick={addOtroImpuesto}
                      className="h-7 gap-1 text-[11px] border-orange-300 text-orange-700 hover:bg-orange-50"
                      data-testid="add-otro-impuesto">
                      <Plus className="w-3 h-3" /> Agregar
                    </Button>
                  </div>
                  {otrosImpuestos.length === 0 ? (
                    <p className="text-[11px] text-gray-500 italic">Sin impuestos adicionales. Click "Agregar" para añadir uno (ej: IEPD $176.587).</p>
                  ) : (
                    <div className="space-y-1.5">
                      {otrosImpuestos.map((oi, idx) => (
                        <div key={idx} className="flex gap-2 items-center" data-testid={`otro-impuesto-row-${idx}`}>
                          <Input value={oi.nombre} onChange={(e) => updateOtroImpuesto(idx, 'nombre', e.target.value)}
                            placeholder="Ej: IEPD - Imp. Específico Petróleo Diesel"
                            className="h-7 text-xs flex-1" data-testid={`otro-impuesto-nombre-${idx}`} />
                          <Input type="number" min="0" value={oi.monto} onChange={(e) => updateOtroImpuesto(idx, 'monto', e.target.value)}
                            placeholder="Monto $" className="h-7 text-xs w-32 text-right font-mono"
                            data-testid={`otro-impuesto-monto-${idx}`} />
                          <Button type="button" size="icon" variant="ghost" onClick={() => removeOtroImpuesto(idx)}
                            className="h-7 w-7 text-red-600 hover:bg-red-50"
                            data-testid={`otro-impuesto-remove-${idx}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Items */}
          <div className="bg-white border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Package className="w-4 h-4" /> 3. Ítems de la OC
              </h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setReqModalOpen(true); loadRequisiciones(); }}
                  data-testid="cargar-requisicion-btn" className="border-purple-300 text-purple-700 hover:bg-purple-50">
                  <ClipboardList className="w-4 h-4 mr-1" /> Cargar Requisicion
                </Button>
                <Button variant="outline" size="sm" onClick={() => setMatModal(true)} data-testid="buscar-material-btn">
                  <Search className="w-4 h-4 mr-1" /> Buscar Material
                </Button>
                <Button size="sm" onClick={() => agregarItem()} className="bg-blue-600 hover:bg-blue-700" data-testid="agregar-item-btn">
                  <Plus className="w-4 h-4 mr-1" /> Agregar Ítem
                </Button>
              </div>
            </div>

            {/* Sugerencias banner */}
            {sugerencias.length > 0 && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between"
                data-testid="sugerencias-banner">
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-amber-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      {sugerencias.reduce((s, g) => s + g.items.length, 0)} items de familias similares en otras requisiciones
                    </p>
                    <p className="text-xs text-amber-600">
                      Familias: {sugFamilias.join(', ')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-100"
                    onClick={() => { setSugChecked({}); setSugModalOpen(true); }} data-testid="ver-sugerencias-btn">
                    Ver sugerencias
                  </Button>
                  <Button size="sm" variant="outline" className="border-blue-400 text-blue-700 hover:bg-blue-100"
                    onClick={() => {
                      const cotizarItems = sugerencias.flatMap(g => g.items.map(it => ({
                        descripcion: it.descripcion || '', cantidad: it.cantidad || 1,
                        unidad: it.unidad || 'UN', codigo: it.codigo_material || '',
                        familia: g.familia || '', ot_numero: String(it.ot_numero || ''),
                        requisicion: String(it.requisicion || ''),
                        observacion: it.observaciones || it.observacion || '',
                      })));
                      sessionStorage.setItem('cotizar_items', JSON.stringify(cotizarItems));
                      navigate('/compras/solicitar-cotizacion?from=sugerencias');
                    }} data-testid="cotizar-sugerencias-btn">
                    Solicitar Cotizacion
                  </Button>
                </div>
              </div>
            )}

            {items.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Agregue ítems desde el maestro de materiales o manualmente</p>
              </div>
            ) : (
              <div className="border rounded-lg" style={{ overflowX: 'clip' }}>
                <table className="w-full text-xs">
                  <thead className="sticky top-14 lg:top-0 z-20">
                    <tr className="bg-gray-800 text-white">
                      <th className="px-2 py-2 w-8">#</th>
                      <th className="px-2 py-2 text-left">Descripcion</th>
                      <th className="px-2 py-2 w-44 text-left">Familia</th>
                      <th className="px-2 py-2 w-16">Und</th>
                      <th className="px-2 py-2 w-36 text-center">OT</th>
                      <th className="px-2 py-2 w-20 text-right">Cant.</th>
                      <th className="px-2 py-2 w-28 text-right">V. Unit.</th>
                      <th className="px-2 py-2 w-28 text-right">Total</th>
                      <th className="px-2 py-2 text-left">Obs</th>
                      <th className="px-2 py-2 w-16 text-center">Precios</th>
                      <th className="px-2 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={item.key} className="border-t hover:bg-gray-50">
                        <td className="px-2 py-1 text-center text-gray-500">{idx + 1}</td>
                        <td className="px-2 py-1">
                          <MaterialAutocomplete
                            value={item.descripcion}
                            onChange={(val) => updateItem(item.key, 'descripcion', val)}
                            onSelect={(mat) => {
                              // Anti-duplicación: solo concatenar descripcion2 si difiere de descripcion
                              const d1 = (mat.descripcion || '').trim();
                              const d2 = (mat.descripcion2 || '').trim();
                              const fullDesc = (d2 && d2 !== d1 && !d1.toLowerCase().includes(d2.toLowerCase())) ? `${d1} ${d2}` : d1;
                              updateItem(item.key, 'descripcion', fullDesc);
                              updateItem(item.key, 'codigo_material', mat.codigo);
                              updateItem(item.key, 'unidad', mat.unidad || 'UND');
                              if (mat.codigo) buscarSugerencias([mat.codigo], null);
                            }}
                            placeholder="Escriba para buscar..."
                          />
                        </td>
                        <td className="px-2 py-1" data-testid={`oc-item-familia-${idx}`}>
                          {item.codigo_material ? (
                            <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" title={`Material ${item.codigo_material}: la cuenta sale del maestro`}>maestro</span>
                          ) : item.cuenta_gasto && !item.subfamilia_codpf ? (
                            <span className="text-[10px] text-slate-600 bg-slate-50 border rounded px-1.5 py-0.5" title={`Cuenta ${item.cuenta_gasto}`}>cuenta {item.cuenta_gasto}</span>
                          ) : (
                            <div className={!item.subfamilia_codpf ? 'rounded ring-1 ring-red-400' : ''}>
                              <SearchableCombobox compact
                                items={subfamiliaItems}
                                value={item.subfamilia_codpf || ''}
                                onChange={(sel) => updateItem(item.key, 'subfamilia_codpf', sel ? String(sel.value) : '')}
                                placeholder="Familia *"
                                emptyMessage="Sin familias"
                                testid={`combo-familia-item-${idx}`} />
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1">
                          <Input value={item.unidad} onChange={(e) => updateItem(item.key, 'unidad', e.target.value)}
                            className="h-7 text-xs text-center" />
                        </td>
                        <td className="px-2 py-1">
                          <OtAutocomplete compact
                            otItems={otsDropdown}
                            value={item.ot_numero || ''}
                            onChange={(val) => updateItem(item.key, 'ot_numero', val)} />
                        </td>
                        <td className="px-2 py-1">
                          <NumberInput value={item.cantidad} onChange={(v) => updateItem(item.key, 'cantidad', parseFloat(v) || 0)}
                            className="h-7 text-xs text-right" />
                        </td>
                        <td className="px-2 py-1">
                          <NumberInput value={item.valor_unitario} onChange={(v) => updateItem(item.key, 'valor_unitario', parseFloat(v) || 0)}
                            className="h-7 text-xs text-right" />
                        </td>
                        <td className="px-2 py-1 text-right font-mono font-medium">
                          ${fmt(item.cantidad * item.valor_unitario)}
                        </td>
                        <td className="px-2 py-1">
                          <Input value={item.observaciones} onChange={(e) => updateItem(item.key, 'observaciones', e.target.value)}
                            className="h-7 text-xs" placeholder="Obs" />
                        </td>
                        <td className="px-2 py-1 text-center">
                          {item.codigo_material && (
                            <button onClick={() => verPrecios(item.codigo_material, item.descripcion)}
                              className="text-blue-600 hover:text-blue-800" title="Ver precios históricos">
                              <History className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                        <td className="px-2 py-1">
                          <button onClick={() => removeItem(item.key)} className="text-red-500 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Iter 287.f — Factura Mixta: tabla de ítems EXENTOS (sin IVA) */}
            {tipoOC === 'mixta' && (
              <div className="mt-4 pt-4 border-t-2 border-teal-300" data-testid="items-exentos-section">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm flex items-center gap-2 text-teal-700">
                    <Package className="w-4 h-4" /> Ítems EXENTOS (sin IVA)
                    <span className="text-[10px] font-normal text-teal-600 bg-teal-50 border border-teal-200 rounded px-2 py-0.5">
                      Facturas COPEC / CGE / servicios con parte exenta
                    </span>
                  </h3>
                  <Button size="sm" onClick={agregarItemExento}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                    data-testid="agregar-item-exento-btn">
                    <Plus className="w-4 h-4 mr-1" /> Agregar Ítem Exento
                  </Button>
                </div>
                {itemsExentos.length === 0 ? (
                  <div className="text-center py-6 text-gray-400 border border-dashed border-teal-200 rounded-lg bg-teal-50/30">
                    <Package className="w-8 h-8 mx-auto mb-2 text-teal-200" />
                    <p className="text-xs">Agregue al menos un ítem exento para la Factura Mixta</p>
                  </div>
                ) : (
                  <div className="border border-teal-200 rounded-lg" style={{ overflowX: 'clip' }}>
                    <table className="w-full text-xs" data-testid="table-items-exentos">
                      <thead>
                        <tr className="bg-teal-700 text-white">
                          <th className="px-2 py-2 w-8">#</th>
                          <th className="px-2 py-2 text-left">Descripción</th>
                          <th className="px-2 py-2 w-16">Und</th>
                          <th className="px-2 py-2 w-20 text-right">Cant.</th>
                          <th className="px-2 py-2 w-28 text-right">V. Unit.</th>
                          <th className="px-2 py-2 w-28 text-right">Total</th>
                          <th className="px-2 py-2 text-left">Obs</th>
                          <th className="px-2 py-2 w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {itemsExentos.map((item, idx) => (
                          <tr key={item.key} className="border-t hover:bg-teal-50/40" data-testid={`row-exento-${idx}`}>
                            <td className="px-2 py-1 text-center text-gray-500">{idx + 1}</td>
                            <td className="px-2 py-1">
                              <Input value={item.descripcion}
                                onChange={(e) => updateItemExento(item.key, 'descripcion', e.target.value)}
                                className="h-7 text-xs" placeholder="Descripción del ítem exento"
                                data-testid={`input-desc-exento-${idx}`} />
                            </td>
                            <td className="px-2 py-1">
                              <Input value={item.unidad}
                                onChange={(e) => updateItemExento(item.key, 'unidad', e.target.value)}
                                className="h-7 text-xs text-center" />
                            </td>
                            <td className="px-2 py-1">
                              <NumberInput value={item.cantidad}
                                onChange={(v) => updateItemExento(item.key, 'cantidad', parseFloat(v) || 0)}
                                className="h-7 text-xs text-right" />
                            </td>
                            <td className="px-2 py-1">
                              <NumberInput value={item.valor_unitario}
                                onChange={(v) => updateItemExento(item.key, 'valor_unitario', parseFloat(v) || 0)}
                                className="h-7 text-xs text-right" />
                            </td>
                            <td className="px-2 py-1 text-right font-mono font-medium text-teal-700">
                              ${fmt(item.cantidad * item.valor_unitario)}
                            </td>
                            <td className="px-2 py-1">
                              <Input value={item.observaciones}
                                onChange={(e) => updateItemExento(item.key, 'observaciones', e.target.value)}
                                className="h-7 text-xs" placeholder="Obs" />
                            </td>
                            <td className="px-2 py-1">
                              <button onClick={() => removeItemExento(item.key)} className="text-red-500 hover:text-red-700"
                                data-testid={`remove-exento-${idx}`}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {itemsExentos.length > 0 && (
                  <p className="text-[10px] text-teal-700 mt-2">
                    ℹ Los ítems exentos NO llevan IVA. Al contabilizar en Alegra usarán la misma cuenta contable y OT que el ítem afecto de mayor monto.
                  </p>
                )}
              </div>
            )}

            {/* Totals */}
            {items.length > 0 && (
              <div className="mt-3 flex justify-end">
                <div className="bg-gray-50 border rounded p-3 text-sm space-y-1 min-w-[280px]" data-testid="totales-resumen">
                  <div className="flex justify-between"><span>Neto:</span><span className="font-mono">${fmt(neto)}</span></div>
                  {iva > 0 && (
                    <div className="flex justify-between"><span>IVA ({ivaPorcentaje}%):</span><span className="font-mono">${fmt(iva)}</span></div>
                  )}
                  {totalOtrosImpuestos > 0 && (
                    <>
                      {otrosImpuestos.filter(o => Number(o.monto) > 0).map((oi, idx) => (
                        <div key={idx} className="flex justify-between text-xs text-orange-700">
                          <span className="truncate max-w-[180px]" title={oi.nombre}>+ {oi.nombre || 'Otro impuesto'}:</span>
                          <span className="font-mono">${fmt(Number(oi.monto))}</span>
                        </div>
                      ))}
                    </>
                  )}
                  {retencionMonto > 0 && (
                    <div className="flex justify-between text-purple-700">
                      <span>− Retención ({retencionPorcentaje}%):</span>
                      <span className="font-mono">−${fmt(retencionMonto)}</span>
                    </div>
                  )}
                  {/* Iter 287.f — Factura Mixta: neto exento (sin IVA) suma al total */}
                  {tipoOC === 'mixta' && netoExento > 0 && (
                    <div className="flex justify-between text-teal-700">
                      <span>+ Neto Exento (sin IVA):</span>
                      <span className="font-mono">${fmt(netoExento)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold border-t pt-1"><span>Total:</span><span className="font-mono">${fmt(total)}</span></div>
                </div>
              </div>
            )}
          </div>

          {/* Save */}
          {items.length > 0 && proveedor && (
            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 flex items-center justify-between">
              <div>
                <p className="font-medium text-blue-800">{items.length} ítem{items.length > 1 ? 's' : ''} | {proveedor.nombre}</p>
                <p className="text-xs text-blue-600">Total: ${fmt(total)}</p>
              </div>
              <Button onClick={handleGuardar} disabled={saving} className="bg-blue-600 hover:bg-blue-700"
                data-testid="guardar-oc-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {isEditMode ? 'Guardar Cambios' : 'Generar OC'}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Material Search Modal */}
      <Dialog open={matModal} onOpenChange={setMatModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Buscar Material</DialogTitle>
          </DialogHeader>
          <MaterialAutocomplete
            value={matSearch}
            onChange={setMatSearch}
            onSelect={(m) => { agregarItem(m); setMatSearch(''); }}
            placeholder="Escriba para buscar en el maestro de materiales..."
            expanded
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setMatModal(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price History Modal */}
      <Dialog open={preciosModal.open} onOpenChange={(o) => setPreciosModal(prev => ({ ...prev, open: o }))}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-600" /> Precios Históricos
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm font-medium text-gray-700 mb-2">{preciosModal.material}</p>
          {preciosModal.loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : preciosModal.precios.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No hay precios históricos</p>
          ) : (
            <table className="w-full text-xs border">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-2 py-1 text-left">Fecha</th>
                  <th className="px-2 py-1 text-left">Proveedor</th>
                  <th className="px-2 py-1 text-right">Cant.</th>
                  <th className="px-2 py-1 text-right">V. Unit.</th>
                  <th className="px-2 py-1">OC</th>
                </tr>
              </thead>
              <tbody>
                {preciosModal.precios.map((p, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1 text-gray-600">{fmtFecha(p.fecha)}</td>
                    <td className="px-2 py-1 truncate max-w-[150px]">{p.proveedor}</td>
                    <td className="px-2 py-1 text-right">{p.cantidad}</td>
                    <td className="px-2 py-1 text-right font-mono font-bold">${fmt(p.valor_unitario)}</td>
                    <td className="px-2 py-1 font-mono text-gray-500">{p.numero_oc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreciosModal(prev => ({ ...prev, open: false }))}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ REQUISICION SELECTOR MODAL ═══ */}
      <Dialog open={reqModalOpen} onOpenChange={setReqModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-purple-600" /> Seleccionar Requisicion
            </DialogTitle>
          </DialogHeader>
          {reqLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-purple-600" /></div>
          ) : reqList.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-8">No hay requisiciones disponibles</p>
          ) : (
            <div className="space-y-2">
              {/* Iter 241 (Carlos #2): buscador de requisiciones */}
              <div className="relative sticky top-0 bg-white pb-2 z-10">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  value={reqSearch}
                  onChange={(e) => setReqSearch(e.target.value)}
                  placeholder="Buscar por N°, solicitante, OT o material…"
                  className="w-full pl-8 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300"
                  data-testid="req-search-input"
                  autoFocus
                />
              </div>
              {(() => {
                const q = reqSearch.trim().toLowerCase();
                const filtered = !q ? reqList : reqList.filter(r =>
                  String(r.numero).toLowerCase().includes(q) ||
                  (r.solicitante || '').toLowerCase().includes(q) ||
                  String(r.ot_numero || '').toLowerCase().includes(q) ||
                  (r.items || []).some(it => (it.descripcion || '').toLowerCase().includes(q) || (it.codigo_material || '').toLowerCase().includes(q))
                );
                if (filtered.length === 0) {
                  return <p className="text-center text-sm text-gray-500 py-6" data-testid="req-search-empty">Sin resultados para “{reqSearch}”</p>;
                }
                return filtered.map(r => (
                <div key={r.numero} onClick={() => cargarRequisicion(r)}
                  className="p-3 border rounded-lg hover:bg-purple-50 cursor-pointer transition-colors"
                  data-testid={`req-option-${r.numero}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-purple-700">#{r.numero}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                        r.estado === 'aprobada' ? 'bg-green-100 text-green-700' : r.estado === 'parcial' ? 'bg-indigo-100 text-indigo-700' : 'bg-amber-100 text-amber-700'
                      }`}>{r.estado}</span>
                      {r.prioridad === 'urgente' && <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-red-100 text-red-700">Urgente</span>}
                    </div>
                    <span className="text-xs text-gray-500">{r.total_items} items</span>
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    <span>Solicitante: <strong>{r.solicitante || '-'}</strong></span>
                    {r.ot_numero ? <span className="ml-3">OT: <strong>{r.ot_numero}</strong></span> : null}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {r.items.slice(0, 3).map((it, j) => (
                      <span key={j} className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded text-gray-600 truncate max-w-[200px]">
                        {it.descripcion}
                      </span>
                    ))}
                    {r.items.length > 3 && <span className="text-[10px] text-gray-400">+{r.items.length - 3} mas</span>}
                  </div>
                </div>
                ));
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ═══ SUGERENCIAS FAMILIA MODAL ═══ */}
      <Dialog open={sugModalOpen} onOpenChange={setSugModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-500" /> Items similares en otras requisiciones
            </DialogTitle>
          </DialogHeader>
          {sugerencias.map((grupo, gi) => (
            <div key={gi} className="border rounded-lg overflow-hidden" data-testid={`sug-group-${gi}`}>
              <div className="bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800 border-b">
                {grupo.concepto} ({grupo.items.length} items)
              </div>
              <table className="w-full text-xs">
                <thead><tr className="bg-gray-50 text-gray-600">
                  <th className="px-2 py-1.5 w-8"></th>
                  <th className="px-2 py-1.5 text-left">Descripcion</th>
                  <th className="px-2 py-1.5 text-center">Cant.</th>
                  <th className="px-2 py-1.5 text-center">Und</th>
                  <th className="px-2 py-1.5 text-center">Req</th>
                  <th className="px-2 py-1.5 text-left">Solicitante</th>
                </tr></thead>
                <tbody>
                  {grupo.items.map((it, ii) => {
                    const key = `${it.requisicion}-${it.codigo_material}`;
                    return (
                      <tr key={ii} className={`border-t hover:bg-gray-50 ${sugChecked[key] ? 'bg-green-50' : ''}`}>
                        <td className="px-2 py-1.5 text-center">
                          <input type="checkbox" checked={!!sugChecked[key]}
                            onChange={(e) => setSugChecked(prev => ({ ...prev, [key]: e.target.checked }))}
                            className="rounded" data-testid={`sug-check-${gi}-${ii}`} />
                        </td>
                        <td className="px-2 py-1.5 font-medium">{it.descripcion}</td>
                        <td className="px-2 py-1.5 text-center font-mono">{it.cantidad}</td>
                        <td className="px-2 py-1.5 text-center">{it.unidad}</td>
                        <td className="px-2 py-1.5 text-center font-mono text-purple-700 font-bold">#{it.requisicion}</td>
                        <td className="px-2 py-1.5 text-gray-600">{it.solicitante}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSugModalOpen(false)}>Cancelar</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={agregarSugeridos}
              disabled={Object.values(sugChecked).filter(Boolean).length === 0} data-testid="btn-agregar-sugeridos">
              Agregar {Object.values(sugChecked).filter(Boolean).length} seleccionados
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
